import { mat4 } from 'gl-matrix';
import { TileArchiveCache } from '../../navigation-mesh/TileArchive';
import { parseObjectsPayload } from '../../scene/TiledObjectStreamer';
import { ParsedModel } from '../../scene/loaders/ModelLoader';
import { TileMeshOutput } from '../../scene/terrain/TileMeshGenerator';
import { TileParseWorkerClient, reconstructBvh } from '../../workers/TileParseWorkerClient';
import { buildBvh } from './BvhBuilder';
import { BlasStore } from './BlasStore';
import { buildTlas, TlasLeaf } from './Tlas';
import { BvhSpatialGrid } from './BvhSpatialGrid';
import { TiledBvhGenerator } from './TiledBvh';
import { Bvh } from './Bvh';

/**
 * Per-tile terrain mesh fetcher. Wraps `TileMeshClient.generate(tx, tz, lod=0)`
 * — same call the renderer uses, so the BVH and the rendered geometry stay in
 * lock-step on resolution, seed, height-sampling, and skirt placement. Pass
 * `null` for scenes without terrain.
 */
export type TerrainMeshFetcher = (tx: number, tz: number) => Promise<TileMeshOutput | null>;

export interface BvhGeneratorConfig {
  archiveCache: TileArchiveCache;
  /** Per-scene BLAS cache; persists across tile loads. */
  blasStore: BlasStore;
  /** Resolves `modelId` → parsed model (with `meshByName`). Frontend uses `Scene.modelRegistry`. */
  resolveModel: (modelId: string) => ParsedModel | undefined;
  /**
   * Per-tile terrain mesh fetcher. Returning null skips the terrain BLAS for
   * that tile — useful when the scene has no terrain or when the tile is
   * outside the published terrain extent.
   */
  fetchTerrainMesh: TerrainMeshFetcher | null;
  /**
   * Optional worker client. When provided, per-tile terrain BLAS construction
   * is offloaded — that's typically the heaviest single piece of per-tile
   * work (~5-10ms for a ~125k-triangle terrain tile), so getting it off the
   * main thread keeps frame budget clean during fast flight. Object BLAS
   * stays on the main thread (sub-millisecond per template, cached after
   * first build).
   */
  workerClient: TileParseWorkerClient | null;
}

/**
 * Returns a `TiledBvhGenerator` that builds a per-tile BVH from:
 *   - terrain triangles via `fetchTerrainMesh` (LOD 0, same as renderer) — one
 *     terrain BLAS per tile, computed fresh each load (cheap: ~1-5ms);
 *   - placed objects from the tile archive's OBJECTS chunk — each placement's
 *     mesh becomes a TLAS leaf referencing a shared BLAS (built once per
 *     `(modelId, meshName)` template and cached in `BlasStore` for reuse
 *     across tiles).
 *
 * Returns `null` for tiles with no terrain and no placements — `TiledBvh`
 * falls back to the heightmap-only path for those.
 *
 * Day-1 implementation: everything runs on the main thread. Worker offload
 * is a follow-up (see plan Phase 7).
 */
export function createTiledBvhGenerator(config: BvhGeneratorConfig): TiledBvhGenerator {
  const { archiveCache, blasStore, resolveModel, fetchTerrainMesh, workerClient } = config;

  return async (tx, tz) => {
    const leaves: TlasLeaf[] = [];

    // --- 1. Terrain BLAS for this tile ---
    if (fetchTerrainMesh) {
      const terrainMesh = await fetchTerrainMesh(tx, tz);
      if (terrainMesh) {
        // `generateTileMesh` returns world-space positions, so the BLAS lives
        // in world coords too — use an identity transform for this leaf.
        // When a worker is wired, build the BLAS off-thread and reconstruct
        // around the transferred buffers; otherwise build inline. The
        // terrain mesh's `positions` and `indices` are detached on transfer,
        // so the caller's mesh object becomes unusable afterwards — but
        // we hold the only reference, so that's fine.
        let terrainBlas: Bvh;
        if (workerClient) {
          const blasResult = await workerClient.buildBlas(
            terrainMesh.positions,
            terrainMesh.indices,
          );
          terrainBlas = reconstructBvh(blasResult);
        } else {
          terrainBlas = buildBvh(terrainMesh.positions, terrainMesh.indices);
        }
        leaves.push({
          blas: terrainBlas,
          transform: mat4.create(),
          inverseTransform: mat4.create(),
          isTerrain: true,
        });
      }
    }

    // --- 2. Object placements from the tile archive ---
    const archive = await archiveCache.get(tx, tz);
    if (archive?.objects) {
      const group = parseObjectsPayload(archive.objects);
      for (const entry of group.entries) {
        const modelRef = group.models[entry.modelRefId];
        if (!modelRef) continue;
        const blas = resolveOrBuildBlas(blasStore, resolveModel, modelRef.model, modelRef.meshName);
        if (!blas) continue;
        const transform = mat4.create();
        mat4.fromRotationTranslationScale(
          transform,
          entry.rotation,
          entry.position,
          [entry.scale, entry.scale, entry.scale],
        );
        const inverseTransform = mat4.create();
        mat4.invert(inverseTransform, transform);
        leaves.push({
          blas,
          transform,
          inverseTransform,
          isTerrain: false,
        });
      }
    }

    if (leaves.length === 0) return null;
    return new BvhSpatialGrid(buildTlas(leaves));
  };
}

/**
 * Look up or build a BLAS for a given template (modelId / meshName). Cached
 * in `BlasStore` keyed by a string-derived numeric hash so subsequent
 * placements of the same template reuse the same BVH.
 *
 * Mesh selection:
 *   - `meshName === '*'` → concatenate ALL meshes of the model into one BLAS.
 *     This is the place-whole-model shortcut (Sponza, building shells, etc.)
 *     that mirrors `TiledObjectStreamer`'s wildcard handling.
 *   - otherwise → look up the single named mesh.
 *
 * Vertex source: **`meshByName` (baked)** is preferred over `rawMeshByName`
 * (unbaked). Static models bake `_MODEL_TRANSFORM` + `_MESH_TRANSFORM` into
 * `meshes` at parse time so the placement transform composes on top of the
 * already-transformed vertices — same shape the renderer uses. Sponza has a
 * 0.01 model-scale that makes `rawMeshes` 100× too large; using rawMeshes
 * for collision puts the BVH triangles far outside the rendered geometry
 * and the player falls through. `rawMeshByName` is the right choice only for
 * animated/skinned meshes where the runtime applies transforms separately
 * (not in this code path — animated objects aren't TLAS leaves).
 *
 * Day-1 uses a simple string-hash for the key (not the backend's SHA-256
 * content hash). Future: when backend bakes BLAS into GLB extras, swap to
 * the existing `stampsByTemplate` map so BLAS keys match voxel-stamp keys.
 */
function resolveOrBuildBlas(
  store: BlasStore,
  resolveModel: (id: string) => ParsedModel | undefined,
  modelId: string,
  meshName: string,
): Bvh | null {
  const key = templateKeyHash(`${modelId}/${meshName}`);
  const cached = store.get(key);
  if (cached) return cached;

  const model = resolveModel(modelId);
  if (!model) return null;
  const meshes =
    meshName === '*' ? model.meshes : (model.meshByName.get(meshName) ? [model.meshByName.get(meshName)!] : []);
  if (meshes.length === 0) return null;

  // Concatenate primitives across all selected meshes into a single
  // (positions, indices) pair. Multi-mesh placements (e.g. Sponza '*')
  // collapse to one BLAS — collision doesn't care about material grouping.
  let totalVerts = 0;
  let totalIndices = 0;
  for (const mesh of meshes) {
    for (const prim of mesh.primitives) {
      const pos = prim.attributes['POSITION'];
      if (!pos) continue;
      totalVerts += pos.array.length / 3;
      totalIndices += prim.indices?.length ?? 0;
    }
  }
  if (totalIndices === 0) return null;

  const positions = new Float32Array(totalVerts * 3);
  const indices = new Uint32Array(totalIndices);
  let vOff = 0;
  let iOff = 0;
  let vertBase = 0;
  for (const mesh of meshes) {
    for (const prim of mesh.primitives) {
      const pos = prim.attributes['POSITION'];
      if (!pos || !prim.indices) continue;
      const posArr = pos.array as Float32Array;
      positions.set(posArr, vOff);
      vOff += posArr.length;
      const primIdx = prim.indices;
      for (let i = 0; i < primIdx.length; i++) {
        indices[iOff++] = primIdx[i] + vertBase;
      }
      vertBase += posArr.length / 3;
    }
  }

  const bvh = buildBvh(positions, indices);
  store.add(key, bvh);
  return bvh;
}

/** Simple stable hash for template keys. Sufficient until backend bakes BLAS. */
function templateKeyHash(s: string): number {
  let h = 5381 | 0;
  for (let i = 0; i < s.length; i++) {
    h = (((h << 5) + h) ^ s.charCodeAt(i)) | 0;
  }
  return h >>> 0;
}
